"use client";

import {useCallback} from "react";
import {useGailGptDictation} from "@/hooks/useGailGptDictation";
import type {ChatInputDictationApi} from "./types";

type ChatInputDictationProviderProps = Readonly<{
   value: string;
   onChange: (value: string) => void;
   disabled?: boolean;
   inputRef: React.RefObject<HTMLTextAreaElement | null>;
   children: (dictation: ChatInputDictationApi) => React.ReactNode;
}>;

/**
 * Mounts the dictation hook only when GailGPT enables it, so shared ChatInput
 * callers (and their tests) do not need AlertContext.
 */
export default function ChatInputDictationProvider({
   value,
   onChange,
   disabled = false,
   inputRef,
   children,
}: ChatInputDictationProviderProps) {
   const focusComposerEnd = useCallback(() => {
      requestAnimationFrame(() => {
         const el = inputRef.current;
         if (!el) return;
         el.focus();
         const end = el.value.length;
         el.setSelectionRange(end, end);
      });
   }, [inputRef]);

   const dictation = useGailGptDictation({
      value,
      onChange,
      disabled,
      onTranscriptInserted: focusComposerEnd,
   });

   return (
      <>
         {children({
            status: dictation.status,
            isSupported: dictation.isSupported,
            toggle: dictation.toggle,
            cancel: dictation.cancel,
         })}
      </>
   );
}
